import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  PackageOpen,
  Pencil,
  Tags,
  Trash2,
} from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type BulkAction,
  type RowAction,
  type SearchPatch,
  type SelectionDescriptor,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type {
  BulkPublishResponse,
  StoreProduct,
  StoreProductListItem,
  StoreProductStatus,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ImportExportLinks } from '@/features/imports-exports';
import {
  MAX_SELECTION_PRODUCTS,
  toListSelection,
  useProductFilterDefs,
  withFirstPageTotal,
} from '@/features/resource-pickers';
import { openStorefrontUrl } from '@/features/stores';
import { productTitles } from '@/shared/bulk/product-titles';
import { useBulkErrorMessage } from '@/shared/bulk/use-bulk-error-message';
import { useKeptTotal } from '@/shared/bulk/use-kept-total';
import { useSelectionRequest } from '@/shared/bulk/use-selection-request';
import { useStore } from '@/shared/store/store-context';

import { displayTitle } from '../api/mappers';
import { AddProductsDialog } from '../components/add-products-dialog';
import { PublishConflictAlert } from '../components/publish-conflict-alert';
import { RemoveStoreProductDialog } from '../components/remove-store-product-dialog';
import { RotationChangeDialog, type RotationChange } from '../components/rotation-change-dialog';
import { useStoreProductColumns } from '../components/store-product-columns';
import { storeProductExtraFilters } from '../components/store-product-filters';
import { useRotationOwners } from '../hooks/use-rotation-owners';
import { useBulkPublish, useUpdateStoreProduct } from '../hooks/use-store-product-mutations';
import { useStoreProducts } from '../hooks/use-store-products';
import { useStoreRotations } from '../hooks/use-store-rotations';
import { groupSkipped, publishConflict } from '../schemas/publication-rules';

const ROUTE_ID = '/_authed/_active/s/$storeId/catalog/' as const;
const ROUTE_PATH = '/s/$storeId/catalog/' as const;

interface BulkOutcome {
  response: BulkPublishResponse;
  /** Sent as a selection ("Select all N"): counted as updated and failed of the matched. */
  matching: boolean;
}

function BulkResult({
  outcome: { response: result, matching },
  rows,
  onDismiss,
}: {
  outcome: BulkOutcome;
  rows: readonly StoreProduct[];
  onDismiss: () => void;
}) {
  const { t, i18n } = useTranslation(['storeCatalog', 'common']);
  const groups = groupSkipped(result);
  // Skipped rows of a selection are mostly not on this page: their titles are looked up.
  const missing = result.skipped
    .map((s) => s.productId)
    .filter((id) => !rows.some((r) => r.productId === id));
  const looked = useQuery({
    queryKey: ['bulk-result-titles', missing],
    queryFn: () => productTitles(missing),
    enabled: missing.length > 0,
    staleTime: Infinity,
    meta: { silentError: true },
  });
  const titleOf = (productId: string) => {
    const row = rows.find((r) => r.productId === productId);
    return row ? displayTitle(row) : (looked.data?.get(productId) ?? productId);
  };
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <Alert variant={groups.length > 0 ? 'destructive' : 'default'} role="status" className="mb-3">
      <AlertTitle>
        {matching
          ? t('common:bulkResult.matchedTitle', {
              succeeded: number.format(result.matched - result.skipped.length),
              failed: number.format(result.skipped.length),
            })
          : t('storeCatalog:bulk.result', { created: result.created, updated: result.updated })}
      </AlertTitle>
      <AlertDescription>
        {groups.length > 0 ? (
          <ul className="list-disc pl-4">
            {groups.map((group) => (
              <li key={group.reason}>
                {t(`storeCatalog:add.result.skipped.${group.reason}`, {
                  count: group.productIds.length,
                })}
                {': '}
                {group.productIds.map(titleOf).join(', ')}
              </li>
            ))}
          </ul>
        ) : null}
        <Button type="button" variant="ghost" size="xs" onClick={onDismiss} className="-ml-2">
          {t('common:actions.close')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function StoreCatalog() {
  const { t } = useTranslation('storeCatalog');
  const store = useStore();
  const queryClient = useQueryClient();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const message = useErrorMessage();
  const bulkMessage = useBulkErrorMessage();
  const selectionRequest = useSelectionRequest();
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const update = useUpdateStoreProduct(store.id);
  const bulk = useBulkPublish(store.id);
  const [removing, setRemoving] = useState<StoreProduct | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkOutcome | null>(null);
  const [conflict, setConflict] = useState<{ productId: string; productStatus: string } | null>(
    null,
  );
  /** A status change on rows a rotation owns, sent once the user confirms it. */
  const [change, setChange] = useState<(RotationChange & { apply: () => void }) | null>(null);
  const rotationOwners = useRotationOwners();
  const storeRotations = useStoreRotations();

  const scope = useMemo(() => ({ kind: 'store' as const, storeId: store.id }), [store.id]);
  const shared = useProductFilterDefs<StoreProductListItem>(scope);
  const extra = useMemo(() => storeProductExtraFilters({ t, queryClient }), [queryClient, t]);
  const filters = useMemo(() => [...shared, ...extra], [extra, shared]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const params = useMemo(() => withFirstPageTotal(list.params), [list.params]);
  const query = useStoreProducts(store.id, params);
  const rows = query.data?.items ?? [];
  // The first page's total, kept while the cursor moves (`cursor: undefined` drops out).
  const counted = useKeptTotal(
    JSON.stringify({ ...list.params, cursor: undefined }),
    query.data,
    query.isPlaceholderData,
  );

  const mutateStatus = update.mutate;
  const setStatus = useCallback(
    (row: StoreProduct, status: StoreProductStatus) => {
      setConflict(null);
      mutateStatus(
        { id: row.id, body: { status } },
        {
          onError: (error) => {
            const refused = publishConflict(error);
            if (refused) setConflict({ productId: row.productId, ...refused });
            else toast.error(message(error));
          },
        },
      );
    },
    [message, mutateStatus],
  );

  /** A row's publish or unpublish; on a row a running rotation owns, after a confirmation. */
  const requestStatus = useCallback(
    async (row: StoreProductListItem, status: StoreProductStatus) => {
      const owners = row.schedule ? await rotationOwners([row]) : [];
      if (owners.length === 0) {
        setStatus(row, status);
        return;
      }
      setChange({ status, owners, total: 1, apply: () => setStatus(row, status) });
    },
    [rotationOwners, setStatus],
  );

  const open = useCallback(
    (row: StoreProduct, hash?: string) =>
      void navigate({
        to: '/s/$storeId/catalog/$storeProductId',
        params: { storeId: store.id, storeProductId: row.id },
        ...(hash ? { hash } : {}),
      }),
    [navigate, store.id],
  );

  const rowActions = useMemo<Array<RowAction<StoreProductListItem>>>(
    () => [
      {
        id: 'open',
        label: t('actions.open'),
        icon: <Pencil aria-hidden />,
        onAction: (row) => open(row),
      },
      {
        id: 'prices',
        label: t('actions.prices'),
        icon: <Tags aria-hidden />,
        onAction: (row) => open(row, 'pricing'),
      },
      {
        id: 'view',
        label: t('actions.view'),
        icon: <ExternalLink aria-hidden />,
        hidden: (row) => !row.storefrontUrl || row.status !== 'published',
        onAction: (row) => openStorefrontUrl(row.storefrontUrl),
      },
      {
        id: 'publish',
        label: t('actions.publish'),
        icon: <Eye aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status === 'published',
        onAction: (row) => requestStatus(row, 'published'),
      },
      {
        id: 'unpublish',
        label: t('actions.unpublish'),
        icon: <EyeOff aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status !== 'published',
        onAction: (row) => requestStatus(row, 'unpublished'),
      },
      {
        id: 'remove',
        label: t('actions.remove'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: () => !canWrite,
        onAction: (row) => setRemoving(row),
      },
    ],
    [canWrite, open, requestStatus, t],
  );

  const columns = useStoreProductColumns(store.id);
  const table = useDataTable({
    mode: 'server',
    data: rows,
    nextCursor: query.data?.nextCursor,
    total: counted.total,
    totalCapped: counted.totalCapped,
    list,
    columns,
    filters,
    // Master product ids (one store product each): an unticked row of "Select all N" goes into
    // the selection's `excludeIds` as it is, from whichever page it was on.
    getRowId: (row) => row.productId,
    tableId: 'store-catalog',
  });

  const bulkPublish = bulk.mutateAsync;
  const runRequest = selectionRequest.run;
  const clearSelection = table.selection.clear;
  const listFilters = list.filters;
  const listQ = list.q;
  const storeId = store.id;
  const bulkActions = useMemo<Array<BulkAction<StoreProductListItem>>>(() => {
    if (!canWrite) return [];
    /** One bulk-publish call on the ticked rows, or on every matching row (a store selection). */
    const publishAll = async (
      selection: SelectionDescriptor,
      selected: StoreProductListItem[],
      status: StoreProductStatus,
    ) => {
      try {
        if (selection.type === 'exclude') {
          const response = await runRequest(
            toListSelection(selection, { filters: listFilters, q: listQ }, 'store'),
            (body) => bulkPublish({ selection: body, status }),
          );
          if (!response) return;
          setBulkResult({ response, matching: true });
        } else {
          const productIds = selected.map((row) => row.productId);
          if (productIds.length === 0) return;
          setBulkResult({ response: await bulkPublish({ productIds, status }), matching: false });
        }
        clearSelection();
      } catch (error) {
        toast.error(bulkMessage(error));
      }
    };
    const action = (id: string, label: string, status: StoreProductStatus, icon: ReactNode) => ({
      id,
      label,
      icon,
      promoted: true,
      supportsAllMatching: true,
      // Cleared here once the change is sent: a cancelled confirmation keeps the selection.
      keepSelection: true,
      onAction: async (selection: SelectionDescriptor, selected: StoreProductListItem[]) => {
        const apply = () => void publishAll(selection, selected, status);
        if (selection.type === 'exclude') {
          // The matching rows are not loaded, so which a rotation owns is unknown: warn that some
          // may be whenever a rotation that reasserts targets the store (or that cannot be read).
          const rotations = await storeRotations(storeId);
          if (rotations !== null && rotations.length === 0) {
            apply();
            return;
          }
          setChange({
            status,
            owners: rotations ?? [],
            total: selection.total - selection.ids.length,
            matching: true,
            apply,
          });
          return;
        }
        if (selected.length === 0) return;
        const owners = selected.some((row) => row.schedule) ? await rotationOwners(selected) : [];
        if (owners.length === 0) {
          apply();
          return;
        }
        setChange({ status, owners, total: selected.length, apply });
      },
    });
    return [
      action('publish', t('bulk.publish'), 'published', <Eye aria-hidden />),
      action('unpublish', t('bulk.unpublish'), 'unpublished', <EyeOff aria-hidden />),
      action('draft', t('bulk.draft'), 'draft', <FileText aria-hidden />),
    ];
  }, [
    bulkMessage,
    bulkPublish,
    canWrite,
    clearSelection,
    listFilters,
    listQ,
    rotationOwners,
    runRequest,
    storeId,
    storeRotations,
    t,
  ]);

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && rows.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={
          <>
            <ImportExportLinks storeId={store.id} kind="products_csv" />
            <Can permission="products.write">
              <AddProductsDialog storeId={store.id} tour />
            </Can>
          </>
        }
      />
      {bulkResult ? (
        <BulkResult outcome={bulkResult} rows={rows} onDismiss={() => setBulkResult(null)} />
      ) : null}
      {conflict ? (
        <div className="mb-3">
          <PublishConflictAlert
            productId={conflict.productId}
            productStatus={conflict.productStatus}
            requestId={isApiError(update.error) ? update.error.requestId : undefined}
          />
        </div>
      ) : null}
      {isEmptyList ? (
        <EmptyState
          tour="store-catalog.list.empty"
          tourLink
          icon={PackageOpen}
          title={t('empty.title')}
          description={t('empty.description')}
          action={
            <Can permission="products.write">
              <AddProductsDialog storeId={store.id} />
            </Can>
          }
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching || update.isPending || bulk.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
          bulkActions={bulkActions}
          allMatchingMax={MAX_SELECTION_PRODUCTS}
          rowActions={rowActions}
          onRowClick={(row) => open(row)}
          getRowLabel={displayTitle}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{ table: 'store-catalog.list.table', filters: 'store-catalog.list.filters' }}
        />
      )}
      <RotationChangeDialog
        change={change}
        onConfirm={() => {
          change?.apply();
          setChange(null);
        }}
        onCancel={() => setChange(null)}
      />
      <RemoveStoreProductDialog
        storeId={store.id}
        row={removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
      />
      {selectionRequest.dialog}
    </PageContent>
  );
}

export function StoreCatalogPage() {
  return (
    <FeatureErrorBoundary name="storeCatalog.list">
      <StoreCatalog />
    </FeatureErrorBoundary>
  );
}
